/**
 * The first two minutes.
 *
 * A new account used to land on an empty Home with nothing to do and no way
 * for anybody to find them - the app knew nothing about what they play, so
 * every list it could show was a list of strangers playing games they may not
 * own.
 *
 * Four questions, all of them skippable, and the answer to each one makes the
 * next screen better: games, where you are, what language, and what you are
 * after. It ends on people and parties that match, not on a congratulation.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { gameIconArt } from '@/lib/gameArt';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { Button, useToast } from './ui';

const DONE_KEY = 'pogu.setup.done';

/** Whether this person still needs the welcome. */
export function firstRunWanted(me: PublicUser | null | undefined): boolean {
  if (!me) return false;
  try { if (localStorage.getItem(DONE_KEY) === '1') return false; } catch { /* storage off */ }
  /* Somebody who has already filled their profile in does not need asking. */
  return !(me.games ?? []).length;
}

export function FirstRun({ onClose }: { onClose: () => void }) {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const patchMe = useSession((s) => s.patchMe);
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [games, setGames] = useState<string[]>(() => (me?.games ?? []).map((g) => g.id));
  const [region, setRegion] = useState(me?.region ?? '');
  const [language, setLanguage] = useState(me?.languages?.[0] ?? 'English');
  const [playstyle, setPlaystyle] = useState(me?.playstyle ?? '');
  const [platforms, setPlatforms] = useState<string[]>(me?.platforms ?? []);
  const [busy, setBusy] = useState(false);

  /* The pay-off screen: real people and real parties, not a placeholder. */
  const matches = useQuery({
    queryKey: ['first-run', games[0] ?? '', region],
    queryFn: () => api.get<{ players: PublicUser[] }>('/api/players?available=1&game=' + (games[0] ?? '')),
    enabled: step === 4
  });

  function finish() {
    try { localStorage.setItem(DONE_KEY, '1'); } catch { /* storage off */ }
    onClose();
  }

  async function save(next: number) {
    setBusy(true);
    try {
      await patchMe({
        games: games.map((id) => ({ id, rank: '' })),
        region: region || undefined,
        languages: [language],
        playstyle: playstyle || undefined,
        platforms
      });
      setStep(next);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const allGames = catalog?.games ?? [];
  const regions = catalog?.regions ?? [];
  const languages = catalog?.languages ?? ['English'];
  const styles = catalog?.playstyles ?? [];

  const steps = ['Games', 'Where', 'Language', 'How you play', 'Ready'];

  return (
    <div className="fixed inset-0 z-[500] grid place-items-center bg-[rgba(0,0,0,.55)] p-4 backdrop-blur-[3px]">
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Welcome to Pugo"
        className="flex max-h-[92vh] w-[min(660px,96vw)] flex-col overflow-hidden rounded-[var(--radius-xl)] bg-surface-1 shadow-[var(--shadow-3)] animate-[rise_240ms_var(--ease-out-soft)]"
      >
        {/* ---- where you are in it ---- */}
        <header className="flex flex-none items-center gap-3 border-b border-line-soft px-5 py-4">
          <img src="./logo.png" alt="" width={34} height={34} className="rounded-[10px]" />
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[18px] font-extrabold tracking-[-.02em] text-ink">
              {step === 4 ? 'You are set up' : 'Welcome to Pugo'}
            </h2>
            <p className="m-0 text-[13px] text-mute">{steps[step]} · {step + 1} of {steps.length}</p>
          </div>
          <button
            type="button"
            onClick={finish}
            className="text-[13px] font-semibold text-mute outline-none transition-colors hover:text-ink"
          >
            Skip
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
          {step === 0 ? (
            <>
              <p className="m-0 text-[14.5px] text-dim">Which games do you actually play? This is how people find you.</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {allGames.map((g) => {
                  const on = games.includes(g.id);
                  const art = gameIconArt(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setGames((list) => on ? list.filter((x) => x !== g.id) : [...list, g.id])}
                      className={cn(
                        'flex flex-col items-center gap-1.5 rounded-[calc(14px*var(--radius-mul,1))] p-2 outline-none transition-[background-color,box-shadow]',
                        on ? 'bg-accent-soft shadow-[inset_0_0_0_1.5px_var(--accent)]' : 'bg-surface-2 hover:bg-surface-3'
                      )}
                    >
                      <span className="h-10 w-10 overflow-hidden rounded-[10px] bg-surface-3">
                        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : null}
                      </span>
                      <span className={cn('line-clamp-2 text-center text-[11.5px] font-semibold', on ? 'text-accent' : 'text-dim')}>{g.name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : step === 1 ? (
            <>
              <p className="m-0 text-[14.5px] text-dim">Where do you play? Parties are matched on region first.</p>
              <div className="flex flex-wrap gap-1.5">
                {regions.map((r) => (
                  <Pick key={r.id} on={region === r.id} onClick={() => setRegion(r.id)}>{r.name}</Pick>
                ))}
              </div>
              <p className="m-0 mt-2 text-[14.5px] text-dim">And on what?</p>
              <div className="flex flex-wrap gap-1.5">
                {([['pc', 'PC'], ['playstation', 'PlayStation'], ['xbox', 'Xbox'], ['switch', 'Switch'], ['mobile', 'Mobile']] as const).map(([id, label]) => {
                  const on = platforms.includes(id);
                  return (
                    <Pick key={id} on={on} onClick={() => setPlatforms((list) => on ? list.filter((x) => x !== id) : [...list, id])}>{label}</Pick>
                  );
                })}
              </div>
            </>
          ) : step === 2 ? (
            <>
              <p className="m-0 text-[14.5px] text-dim">What do you speak in a call?</p>
              <div className="flex flex-wrap gap-1.5">
                {languages.map((l) => (
                  <Pick key={l} on={language === l} onClick={() => setLanguage(l)}>{l}</Pick>
                ))}
              </div>
            </>
          ) : step === 3 ? (
            <>
              <p className="m-0 text-[14.5px] text-dim">What are you usually after? It goes on your profile and on the parties you post.</p>
              <div className="flex flex-wrap gap-1.5">
                {styles.map((p) => (
                  <Pick key={p.id} on={playstyle === p.name} onClick={() => setPlaystyle(p.name)}>{p.name}</Pick>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="m-0 text-[14.5px] text-dim">
                {matches.data?.players?.length
                  ? 'People who are free to play right now:'
                  : 'Nobody is marked free to play this minute. The parties list is the other way in.'}
              </p>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {(matches.data?.players ?? []).slice(0, 5).map((u) => (
                  <li key={u.id} className="flex items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-2">
                    <Avatar user={u} size={34} presence />
                    <UserName user={u} className="min-w-0 flex-1 truncate text-[13.5px] font-semibold" />
                    <Button size="sm" kind="outline" onClick={() => { finish(); go('u/' + u.username); }}>Look</Button>
                  </li>
                ))}
              </ul>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Button kind="primary" icon={<Icon name="squads" size={16} />} onClick={() => { finish(); go('parties'); }}>
                  Find a party
                </Button>
                <Button kind="outline" icon={<Icon name="plus" size={16} />} onClick={() => { finish(); go('parties?create=1'); }}>
                  Post one yourself
                </Button>
              </div>
            </>
          )}
        </div>

        <footer className="flex flex-none items-center gap-2 border-t border-line-soft px-5 py-4">
          {step > 0 && step < 4 ? <Button kind="plain" onClick={() => setStep(step - 1)}>Back</Button> : null}
          <span className="flex-1" />
          {step < 4 ? (
            <Button
              kind="primary"
              disabled={busy || (step === 0 && !games.length)}
              onClick={() => (step === 3 ? void save(4) : void save(step + 1))}
            >
              {busy ? 'Saving…' : step === 3 ? 'Finish' : 'Next'}
            </Button>
          ) : (
            <Button kind="primary" onClick={finish}>Start using Pugo</Button>
          )}
        </footer>
      </section>
    </div>
  );
}

function Pick({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'h-9 rounded-full px-3.5 text-[13px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}
